import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { MaskedLines } from "@/components/MaskedLines";
import { Reveal } from "@/components/Reveal";
import { PackageCard } from "@/components/PackageCard";
import { PageLoader } from "@/components/PageLoader";
import { Seo } from "@/components/Seo";
import { QuestionForm } from "@/components/QuestionForm";
import { useConfig } from "@/context/ConfigContext";
import { scrollToHash } from "@/lib/scroll";
import { inr } from "@/lib/format";
import { TID } from "@/lib/testIds";

export default function Pricing() {
  const { config, loading } = useConfig();
  const location = useLocation();
  const [months, setMonths] = useState(0);

  const offers = config.offers || { enabled: false, options: [] };
  const discount = offers.enabled ? offers.options.find((o) => o.months === months)?.discount || 0 : 0;

  useEffect(() => {
    if (!location.hash) return;
    const t = setTimeout(() => scrollToHash(location.hash), 400);
    return () => clearTimeout(t);
  }, [location.hash]);

  if (loading) return <PageLoader />;

  const pkgs = config.packages || [];
  const plan = config.maintenance_plan || { name: "Premium Maintenance", price: 6999, intro: "", features: [] };
  const notes = config.notes || {};

  const toggleOptions = [{ m: 0, label: "No commitment", sub: "standard pricing", months: 0 }].concat(
    (offers.options || []).map((o) => ({
      m: o.months,
      label: `${o.months}-month term`,
      sub: `saves ${inr(o.discount)} on setup`,
      months: o.months,
    }))
  );

  return (
    <main>
      <Seo
        title="Website Design Packages & Pricing — OFFCRAFT"
        description="Compare OFFCRAFT's three website packages — Essential, Signature and Elevate. One-time development fee and monthly maintenance shown separately. No hidden fees."
      />
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-ink/10">
        <div className="grid-bg pointer-events-none absolute inset-0" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-6 pb-16 pt-28 lg:pb-20 lg:pt-40">
          <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-crimson">Packages & pricing</p>
          <MaskedLines
            ariaLabel="Three packages. Zero ambiguity."
            delay={0.25}
            className="mt-6 font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-ink sm:text-6xl lg:text-7xl"
            lines={[<>Honest pricing,</>, <>zero <span className="text-crimson">surprises.</span></>]}
          />
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-ink/60 sm:text-lg">
            Development and maintenance, shown separately and invoiced exactly as displayed. No hidden fees — ever.
          </p>
        </div>
      </section>

      {/* CONTRACT COMMITMENT CALCULATOR */}
      {offers.enabled && (
        <section className="mx-auto max-w-7xl px-6 pt-16" data-testid={TID.pricing.offerBlock}>
          <Reveal>
            <div className="mx-auto max-w-3xl">
              <p className="text-center font-mono text-[11px] uppercase tracking-[0.35em] text-ink/65">
                {offers.title || "Commitment credit"} — optional, always disclosed
              </p>
              <div className="mt-5 flex flex-col border border-forest/10 bg-surface sm:flex-row">
                {toggleOptions.map((o) => (
                  <button
                    key={o.m}
                    data-testid={TID.pricing.toggle(o.m)}
                    onClick={() => setMonths(o.m)}
                    className={`flex-1 px-6 py-4 text-left font-mono text-xs font-bold uppercase tracking-[0.15em] transition-colors sm:text-center ${
                      months === o.m ? "bg-crimson text-ink" : "text-ink/60 hover:text-crimson"
                    }`}
                  >
                    {o.label}
                    <span className={`block text-[9px] normal-case tracking-[0.15em] ${months === o.m ? "text-white/75" : "text-ink/60"}`}>{o.sub}</span>
                  </button>
                ))}
              </div>
              <p className="mt-4 text-center font-mono text-[11px] uppercase leading-relaxed tracking-[0.15em] text-ink/60">{offers.disclosure}</p>
            </div>
          </Reveal>
        </section>
      )}

      {/* PACKAGES */}
      <section className="mx-auto max-w-7xl px-6 pb-24 pt-14 lg:pt-20">
        <div className="grid items-stretch gap-6 sm:gap-8 lg:grid-cols-3 lg:gap-8">
          {pkgs.map((p, i) => (
            <PackageCard key={p.id} pkg={p} discount={discount} months={months} index={i} />
          ))}
        </div>
        {pkgs.length === 0 && (
          <p className="font-mono text-xs text-ink/60">Packages are being updated — check back shortly.</p>
        )}
      </section>

      {/* MAINTENANCE */}
      <section id="maintenance" className="relative scroll-mt-24 border-y border-crimson/25 bg-forest">
        <div className="grid-bg pointer-events-none absolute inset-0 opacity-50" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:py-28">
          <div className="grid items-center gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <Reveal>
                <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-white/70">Optional — by enquiry only</p>
                <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
                  {plan.name}. Kept sharp, long after launch.
                </h2>
                <p className="mt-5 max-w-xl text-base leading-relaxed text-white/75">{plan.intro}</p>
                <div className="mt-8 flex flex-wrap gap-4">
                  <Link
                    data-testid={TID.pricing.maintenanceCta}
                    to="/contact?package=maintenance"
                    className="group inline-flex items-center gap-2 bg-crimson px-8 py-4 font-display text-sm font-bold text-white transition-colors hover:bg-crimsondark"
                  >
                    Enquire about this plan
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </Link>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.1}>
                <div data-testid={TID.pricing.maintenanceCard} className="border border-white/15 bg-black/25 p-8 backdrop-blur-sm">
                  <div className="font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-white/60">From</div>
                  <div className="mt-2 font-display text-4xl font-extrabold text-white">
                    {inr(plan.price)}
                    <span className="font-body text-base font-medium text-white/60"> /month</span>
                  </div>
                  <ul className="mt-6 space-y-3">
                    {(plan.features || []).slice(0, 4).filter(Boolean).map((f, i) => (
                      <li key={i} className="flex gap-3 text-sm text-white/85">
                        <span className="mt-2 inline-block h-1.5 w-1.5 shrink-0 rotate-45 bg-crimson" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-6 border-l-2 border-crimson/70 pl-3 text-xs leading-relaxed text-white/70">{plan.note}</p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
